import { Link, Navigate } from "react-router-dom";
import { useAuth } from "../auth/index.js";

export function Landing() {
   const { isAuthenticated, loading } = useAuth();

   if (!loading && isAuthenticated) return <Navigate to="/dashboard" replace />;

   return (
      <div className="min-h-full bg-white">
         {/* Nav */}
         <nav className="flex items-center justify-between px-6 py-4 max-w-5xl mx-auto">
            <span className="text-xl font-bold text-primary-700">
               Launchpad
            </span>
            <div className="flex gap-3">
               <Link
                  to="/login"
                  className="rounded px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-100"
               >
                  Sign In
               </Link>
               <Link
                  to="/login?mode=signup"
                  className="rounded bg-primary-600 px-4 py-2 text-sm font-medium text-white hover:bg-primary-700"
               >
                  Get Started
               </Link>
            </div>
         </nav>

         {/* Hero */}
         <section className="px-6 py-20 text-center max-w-3xl mx-auto">
            <h1 className="text-4xl sm:text-5xl font-bold text-slate-900 leading-tight">
               Know exactly what to do
               <br />
               next in your job search
            </h1>
            <p className="mt-4 text-lg text-slate-600 max-w-xl mx-auto">
               Launchpad surfaces your overdue follow-ups, stale applications,
               and missing next steps — so you act instead of wondering what
               fell through the cracks.
            </p>
            <div className="mt-8 flex justify-center gap-4">
               <Link
                  to="/login?mode=signup"
                  className="rounded-lg bg-primary-600 px-6 py-3 text-sm font-semibold text-white hover:bg-primary-700 shadow-sm"
               >
                  Start Tracking
               </Link>
            </div>
         </section>

         {/* Demo Video */}
         <section className="px-6 pb-16 max-w-3xl mx-auto">
            <div className="relative mx-auto" style={{ maxWidth: "680px" }}>
               {/* Screen */}
               <div className="rounded-t-xl bg-gray-800 p-2 pb-0">
                  <div className="flex items-center gap-1.5 px-2 pb-2">
                     <span className="size-2.5 rounded-full bg-red-400" />
                     <span className="size-2.5 rounded-full bg-yellow-400" />
                     <span className="size-2.5 rounded-full bg-green-400" />
                  </div>
                  <video
                     src="/launchPadDemo.mp4"
                     autoPlay
                     loop
                     muted
                     playsInline
                     className="w-full rounded-t-sm"
                  />
               </div>
               {/* Base */}
               <div className="h-4 rounded-b-xl bg-linear-to-b from-gray-700 to-gray-800" />
               <div className="mx-auto h-3 w-1/4 rounded-b-lg bg-gray-700" />
            </div>
         </section>

         {/* Features */}
         <section className="px-6 py-16 bg-slate-50">
            <div className="max-w-4xl mx-auto">
               <h2 className="text-2xl font-bold text-slate-900 text-center mb-10">
                  Everything you need to manage your job search
               </h2>
               <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                  {[
                     [
                        "▦ Kanban Board",
                        "Drag-and-drop cards across Saved, Applied, Interviewing, Offer, and Rejected columns.",
                     ],
                     [
                        "🤝 Networking Pipeline",
                        "Track outreach, conversations, and relationships on a dedicated board — separate from applications.",
                     ],
                     [
                        "🎯 Goals & Streaks",
                        "Set weekly targets, track progress with auto-computed metrics, and build streaks to stay motivated.",
                     ],
                     [
                        "◫ Dashboard",
                        "Response rate, pipeline funnel, goal progress, stale relationship alerts, and due-today reminders.",
                     ],
                     [
                        "⚡ Quick Clip",
                        "Save jobs instantly from any browser tab without leaving the page.",
                     ],
                     [
                        "📱 Mobile Ready",
                        "Responsive design with status tabs and full-screen modals on small screens.",
                     ],
                     [
                        "✉ Follow-up Tracking",
                        "Last Contacted date and stale application nudges so nothing falls through the cracks.",
                     ],
                     [
                        "📋 Interview Prep",
                        "Per-app checklists, structured debrief notes, and Problem-Action-Result story practice with spaced repetition.",
                     ],
                     [
                        "🔗 Share Progress",
                        "Generate a read-only link for mentors and coaches to see your pipeline at a glance.",
                     ],
                  ].map(([title, desc]) => (
                     <div
                        key={title}
                        className="rounded-lg border border-slate-200 bg-white p-5"
                     >
                        <h3 className="text-sm font-semibold text-slate-900">
                           {title}
                        </h3>
                        <p className="mt-1 text-sm text-slate-600">{desc}</p>
                     </div>
                  ))}
               </div>
            </div>
         </section>

         {/* Coach section */}
         <section className="px-6 py-16 bg-white">
            <div className="max-w-4xl mx-auto">
               <div className="grid grid-cols-1 sm:grid-cols-2 gap-10 items-center">
                  <div>
                     <p className="text-xs font-semibold text-primary-600 uppercase tracking-wide mb-2">
                        For Career Coaches
                     </p>
                     <h2 className="text-2xl font-bold text-slate-900">
                        See your clients' progress without the check-in
                     </h2>
                     <p className="mt-3 text-sm text-slate-600 leading-relaxed">
                        Your clients share a read-only link that shows their
                        networking pipeline, weekly goals, interview rate, and
                        what needs attention — updated in real time. No signup
                        required on your end.
                     </p>
                     <ul className="mt-4 space-y-2 text-sm text-slate-700">
                        <li className="flex items-start gap-2">
                           <span className="text-primary-500 mt-0.5">✓</span>
                           <span>
                              See who's actually doing outreach vs. stalling
                           </span>
                        </li>
                        <li className="flex items-start gap-2">
                           <span className="text-primary-500 mt-0.5">✓</span>
                           <span>
                              Goal progress visible — no more "how many did you
                              send?"
                           </span>
                        </li>
                        <li className="flex items-start gap-2">
                           <span className="text-primary-500 mt-0.5">✓</span>
                           <span>
                              "Needs Attention" section tells you what to
                              discuss in your next session
                           </span>
                        </li>
                        <li className="flex items-start gap-2">
                           <span className="text-primary-500 mt-0.5">✓</span>
                           <span>
                              Zero setup — just open the link your client shares
                           </span>
                        </li>
                     </ul>
                     <Link
                        to="/login?mode=signup"
                        className="mt-6 inline-block rounded-lg bg-primary-600 px-5 py-2.5 text-sm font-semibold text-white hover:bg-primary-700"
                     >
                        Recommend to a Client
                     </Link>
                  </div>
                  <div className="rounded-lg border border-slate-200 bg-slate-50 p-5 space-y-3">
                     <p className="text-xs font-medium text-slate-500">
                        What you'll see:
                     </p>
                     <div className="rounded bg-white border border-slate-200 p-3 space-y-2">
                        <div className="flex justify-between text-xs">
                           <span className="text-slate-600">
                              Outreach to 5 people this week
                           </span>
                           <span className="font-medium text-primary-600">
                              3/5
                           </span>
                        </div>
                        <div className="h-1.5 bg-slate-100 rounded overflow-hidden">
                           <div
                              className="h-full bg-primary-500 rounded"
                              style={{ width: "60%" }}
                           />
                        </div>
                     </div>
                     <div className="rounded bg-white border border-slate-200 p-3 space-y-2">
                        <div className="flex justify-between text-xs">
                           <span className="text-slate-600">
                              Schedule 2 conversations
                           </span>
                           <span className="font-medium text-green-600">
                              ✓ 2/2
                           </span>
                        </div>
                        <div className="h-1.5 bg-slate-100 rounded overflow-hidden">
                           <div
                              className="h-full bg-green-500 rounded"
                              style={{ width: "100%" }}
                           />
                        </div>
                     </div>
                     <div className="rounded bg-white border border-slate-200 p-3">
                        <p className="text-xs font-medium text-slate-700 mb-1">
                           Needs Attention
                        </p>
                        <p className="text-xs text-red-600">
                           2 overdue follow-ups
                        </p>
                        <p className="text-xs text-amber-600">
                           1 needs next step
                        </p>
                     </div>
                  </div>
               </div>
            </div>
         </section>

         {/* CTA */}
         <section className="px-6 py-16 text-center bg-primary-600">
            <h2 className="text-2xl font-bold text-white">
               Ready to organize your job search?
            </h2>
            {/* <p className="mt-2 text-primary-100">
               Free to use. No credit card required.
            </p> */}
            <Link
               to="/login?mode=signup"
               className="mt-6 inline-block rounded-lg bg-white px-6 py-3 text-sm font-semibold text-primary-700 hover:bg-primary-50 shadow-sm"
            >
               Get Started
            </Link>
         </section>

         {/* Footer */}
         <footer className="px-6 py-6 text-center bg-slate-50 border-t border-slate-200">
            <Link to="/privacy" className="text-xs text-slate-400 hover:text-slate-600">
               Privacy Policy
            </Link>
         </footer>
      </div>
   );
}
